import React, { memo } from 'react';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment-timezone';
import Utils from '../../../utils/utils';
import { GLOBAL_CALENDAR_SHIFT_TYPES, PERMISSION } from '../../../constants';
import { SHIFT_ROUTES, SHIFT_TYPES } from '../../../constants/ShiftStatus';
import Button from '../../../components/button/Button';
import IsAuthorized from '../../../components/IsAuthorized/IsAuthorized';
import arrow from '../../../assets/images/arrow.svg';

const AddShiftsButton = ({
	currentFacility,
	user,
	shift,
	shiftType,
	delinquentAccountReceivableStatus,
	fromGlobalOverview,
	handleRequestShift,
	setShowAddSlotVendorWarningModal
}) => {
	if (delinquentAccountReceivableStatus) {
		return (
			<OverlayTrigger
				trigger={['hover', 'focus']}
				placement="top"
				overlay={
					<Tooltip show className="accounts-receivable-tooltip">
						This button is disabled due to non-payment
					</Tooltip>
				}
			>
				<div className="button-container">
					<Button
						exactPermission={PERMISSION.EDIT}
						className="add-slot-button add-request-slot-button text-nowrap read-only"
						onClick={() => setShowAddSlotVendorWarningModal(true)}
						title="+ Add Shift"
					/>
				</div>
			</OverlayTrigger>
		);
	}
	const hasAllRoutes =
		shift.shift_routes.has(GLOBAL_CALENDAR_SHIFT_TYPES.FLEX) &&
		shift.shift_routes.has(GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND);
	const userHasOnDemandPermission =
		Utils.isAuthorizedExact(PERMISSION.EDIT, user.permissions) && currentFacility.allow_on_demand_shifts;
	const userHasFlexPermission =
		Utils.isAuthorizedExact(PERMISSION.FLEX_ADD_EDIT_SHIFT, user.permissions) && currentFacility.allow_flex_shifts;

	const shouldShowHybrid = () => {
		const canShowHybrid = currentFacility.enable_hybrid_shifts && hasAllRoutes && !shift.isOnCall;
		const hasAllPermissions = canShowHybrid && userHasFlexPermission && userHasOnDemandPermission;
		return shift.start_time_at_utc
			? moment.utc(shift.start_time_at_utc).add(2, 'hours').isAfter(moment.utc()) && hasAllPermissions
			: hasAllPermissions;
	};

	const shouldShowAddButton = (isFlex) => {
		const showOnlyFlexType = shiftType === GLOBAL_CALENDAR_SHIFT_TYPES.FLEX && fromGlobalOverview;
		const showOnlyOnDemandType =
			(shiftType === GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND && fromGlobalOverview) || shift.isOnCall;

		const showOnlyHybrid = shiftType === SHIFT_TYPES.HYBRID && fromGlobalOverview;

		if (showOnlyHybrid) {
			return false;
		}

		if (isFlex) {
			if (showOnlyOnDemandType) {
				return false;
			}
			return shift.end_time_at_utc
				? moment.utc(shift.end_time_at_utc).diff(moment.utc(), 'minutes') > 15 && userHasFlexPermission
				: userHasFlexPermission;
		}
		if (showOnlyFlexType) {
			return false;
		}

		const shouldShowOnDemand = shift.start_time_at_utc
			? moment.utc(shift.start_time_at_utc).add(2, 'hours').isAfter(moment.utc()) && userHasOnDemandPermission
			: userHasOnDemandPermission;

		if (shift.isOnCall) {
			return (
				shouldShowOnDemand &&
				Utils.isAuthorizedExact('PORTAL.ON_CALL.EDIT', user.permissions) &&
				currentFacility.allow_on_call
			);
		}
		return shouldShowOnDemand;
	};

	const showFlex = shouldShowAddButton(true);
	const showOnDemand = shouldShowAddButton(false);
	const showHybrid = shouldShowHybrid();

	const hasToShowAtLeastOne = showFlex || showOnDemand || showHybrid;

	if (!hasToShowAtLeastOne) {
		return <> </>;
	}

	if (showHybrid && hasAllRoutes && !showOnDemand && !showFlex && !shift.is_orientation) {
		return (
			<div className="button-container">
				<Button
					className="add-slot-button add-request-slot-button text-nowrap"
					onClick={() => handleRequestShift(SHIFT_ROUTES.HYBRID)}
					exactPermission={PERMISSION.EDIT}
					title="+ Add Shift"
				/>
			</div>
		);
	}

	if ((showFlex && !showOnDemand) || (!showFlex && showOnDemand)) {
		return (
			<div className="button-container">
				<Button
					exactPermission={showFlex ? PERMISSION.FLEX_ADD_EDIT_SHIFT : PERMISSION.EDIT}
					className="add-slot-button add-request-slot-button text-nowrap"
					onClick={() => handleRequestShift(showFlex ? SHIFT_ROUTES.FLEX : SHIFT_ROUTES.SHIFTMED)}
					title="+ Add Shift"
				/>
			</div>
		);
	}

	const canAddHybrid =
		userHasOnDemandPermission && userHasFlexPermission && currentFacility.enable_hybrid_shifts && !shift.is_orientation;

	return (
		<div className="button-container">
			<Dropdown className="request-slot-dropdown">
				<Dropdown.Toggle className="add-dropdown-slot-toggle text-nowrap" id="dropdown-basic">
					+ Add Shift
					<img src={arrow} alt="expand" className="arrow-icon" />
				</Dropdown.Toggle>
				<Dropdown.Menu className="request-shifts-ds-toggle__menu">
					<IsAuthorized exactPermission="PORTAL.EDIT">
						<Dropdown.Item
							className="request-shifts-ds-toggle__menu-item"
							onClick={() => handleRequestShift(SHIFT_ROUTES.SHIFTMED)}
						>
							Add On Demand Shift
						</Dropdown.Item>
					</IsAuthorized>
					<IsAuthorized exactPermission="PORTAL.FLEX.ADD_EDIT_SHIFT">
						<Dropdown.Item
							className="request-shifts-ds-toggle__menu-item"
							onClick={() => handleRequestShift(SHIFT_ROUTES.FLEX)}
						>
							Add Internal Flex Shift
						</Dropdown.Item>
					</IsAuthorized>
					{canAddHybrid && (
						<Dropdown.Item
							className="request-shifts-ds-toggle__menu-item"
							onClick={() => handleRequestShift(SHIFT_ROUTES.HYBRID)}
						>
							Add Hybrid Shift
						</Dropdown.Item>
					)}
				</Dropdown.Menu>
			</Dropdown>
		</div>
	);
};

export default memo(AddShiftsButton);
